import type { Contract } from 'validata';
import { asDate, isNumber, isObject, isString, maybeString } from 'validata';
import { isObjectSet } from 'validata-mongo';
import type {
  AddGuestRequest,
  FinishPatrolRequest,
  RecordIncidentRequest,
  StartPatrolRequest,
} from './patrol.api.js';
import type { RequestContext } from './request-context.js';

export enum PatrolType {
  Patrol = 'patrol',
  Event = 'event',
}

export namespace Patrol {
  export const NAME = 'patrol' as const;

  export type Assignment = {
    accepted?: RequestContext;
    added: RequestContext;
    declined?: RequestContext;
    didNotShow?: RequestContext;
    emailed?: RequestContext;
    removed?: RequestContext;
    removeEmailed?: RequestContext;
  };

  export type GuestAssignment = AddGuestRequest & {
    added: RequestContext;
    emailed?: RequestContext;
    removed?: RequestContext;
    removeEmailed?: RequestContext;
  };

  export type StartRecord = StartPatrolRequest & {
    administrative?: boolean;
    request: RequestContext;
  };

  export type PatrolVehicleInfo = {
    rego: string;
    make: string;
    model: string;
    color: string;
    hasLivery: boolean;
  };

  export type EccLogOnNotification = {
    hasPoliceRadio: boolean;
    email?: { messageId: string };
    request: RequestContext;
    vehicle?: PatrolVehicleInfo;
    when: Date;
  };

  export type EccResponse = {
    email: { messageId?: string };
    when: Date;
  };

  export type EccLogOnResponse = EccResponse & {
    policeEventNumber?: string;
  };

  export type FinishRecord = FinishPatrolRequest & {
    administrative?: boolean;
    request: RequestContext;
  };

  export type EccLogOffNotification = {
    email?: { messageId: string };
    when: Date;
  };

  export type Incident = RecordIncidentRequest & {
    _id: string;
    request: RequestContext;
    whenFinished?: Date;
  };

  export type Type = {
    _id: string;
    callSign?: string;
    cancelled?: RequestContext;
    deleted?: RequestContext;
    created: RequestContext;
    eccLogOffNotification?: EccLogOffNotification;
    eccLogOffResponse?: EccResponse;
    eccLogOnNotification?: EccLogOnNotification;
    eccLogOnResponse?: EccLogOnResponse;
    finalized?: RequestContext;
    finished?: FinishRecord;
    guests?: Record<string, GuestAssignment>;
    hours: number;
    incidents?: Incident[];
    name: string;
    organizationId: string;
    patrollers?: Record<string, Assignment>;
    primaryPatroller?: string; // personId
    replyTo?: string;
    started?: StartRecord;
    type?: 'patrol' | 'event';
    instructions?: string;
    when: Date;
  };

  export type CreateEvent = Pick<
    Type,
    'hours' | 'name' | 'when' | 'instructions'
  >;
  export type CreatePatrol = Pick<Type, 'callSign' | 'hours' | 'name' | 'when'>;
  export type Patch = Pick<Type, 'hours' | 'name' | 'when' | 'instructions'>;

  export const createEventContract: Contract<CreateEvent> = {
    hours: isNumber({ min: 1 }),
    instructions: maybeString(),
    name: isString({ minLength: 3, trim: 'both' }),
    when: asDate(),
  };

  export const createPatrolContract: Contract<CreatePatrol> = {
    callSign: isString({
      minLength: 2,
      transform: (value) => value.toLocaleUpperCase(),
      trim: 'both',
    }),
    hours: isNumber({ min: 1 }),
    name: isString({ minLength: 3, trim: 'both' }),
    when: asDate(),
  };

  export const createEvent = isObject<CreateEvent>(createEventContract);
  export const createPatrol = isObject<CreatePatrol>(createPatrolContract);

  export const patchContract: Contract<Patch> = {
    hours: isNumber({ min: 1 }),
    instructions: maybeString(),
    name: isString({ minLength: 3, trim: 'both' }),
    when: asDate(),
  };

  export const patch = isObjectSet<Patch>(patchContract);
}
